<template>
  <div>
    <v-layout class="wrapper" nowrap :class="[styleClasses, { disabled: disabled }]">
      <v-flex
        shrink
      >
        <div
          :style="inputBorderStyle"
          aria-label="Country Code Selector"
          v-click-outside="clickedOutside"
          :class="[ 'cursor-pointer', 'vti__dropdown', { open: open, disabled: dropdownOptions.disabled }]"
          :tabindex="dropdownOptions.tabindex"
          @keydown="keyboardNav"
          @click="openDropdown"
        >
          <v-select
            class="country-selector"
            v-model="selectedCountry"
            :items="sortedCountries"
            :disabled="dropdownOptions.disabled"
            :menu-props="{ value: open, width: dropdownOptions.width, maxWidth: dropdownOptions.width }"
            ref="countrySelector"
            flat
          >
            <template v-slot:prepend-item>
              <v-text-field
                v-if="dropdownOptions.showSearchBox"
                v-model="searchQuery"
                class="search-country vti__input vti__search_box"
                placeholder="Enter country name / country code"
                ref="countrySearch"
                @keydown="searchCountryKeydown"
              />
            </template>
            <template v-slot:item="{item}">
              <v-layout
                align-bottom
                :class="['vti__dropdown-item', {'primary--text': (selectedCountry && selectedCountry.dialCode == item.dialCode)}]"
              >
                <v-flex shrink style="display: block;">
                  <span
                    v-if="dropdownOptions.showFlags"
                    :class="['vti__flag', item.iso2.toLowerCase()]"
                  />
                </v-flex>
                <v-flex shrink w-75>
                  <span>{{ item.name }}</span>
                </v-flex>
                <v-flex shrink>
                  <span v-if="dropdownOptions.showDialCodeInList"> +{{ item.dialCode }} </span>
                </v-flex>
              </v-layout>
            </template>
            <template v-slot:selection="{item}">
              <span class="vti__selection  pt-2">
                <span
                  v-if="dropdownOptions.showFlags"
                  :class="['vti__flag', item.iso2.toLowerCase()]"
                />
                <span v-if="dropdownOptions.showDialCodeInSelection" class="vti__country-code ml-1" style="font-size: 16px;">
                  +{{ item && item.dialCode }}
                </span>
              </span>
            </template>
          </v-select>
        </div>
      </v-flex>

      <v-flex
        :style="inputBorderStyle"
        class="ml-2"
      >
        <v-layout>
          <v-flex :class="{ 'xs8': $slots['icon-right'] }">
            <input
              v-model="phone"
              ref="input"
              :type="inputOptions.type"
              :autocomplete="inputOptions.autocomplete"
              :autofocus="inputOptions.autofocus"
              :class="['vti__input', inputOptions.styleClasses]"
              style="font-size: 16px;"
              :disabled="disabled"
              :id="inputOptions.id"
              :maxlength="inputOptions.maxlength"
              :name="inputOptions.name"
              :placeholder="parsedPlaceholder"
              :readonly="inputOptions.readonly"
              :required="inputOptions.required"
              :tabindex="inputOptions.tabindex"
              :aria-describedby="inputOptions['aria-describedby']"
              @blur="onBlur"
              @focus="onFocus"
              @input="onInput"
            >
          </v-flex>
          <v-flex v-if="$slots['icon-right']" xs4>
            <slot name="icon-right" />
          </v-flex>
          <v-flex v-if="businessDetails && businessDetails.shortenFlow && $vuetify.breakpoint.xsOnly && showIcon">
            <v-icon color="primary" @click="disableIcon">
              mdi-check
            </v-icon>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
    <v-layout>
      <v-flex v-if="invalidMsg && (!phoneObject.valid) && showInvalidMessage" style="color: red; font-size: 12px" class="mt-1">
        {{ invalidMsg }}
      </v-flex>
      <v-flex v-if="forSummary && !showInvalidMessage" style="font-size: 12px" class="mt-1">
        <span>
          Verified number: <span><a @click="updatePhoneNumber()" style="text-decoration: underline;">{{ verifiedNumberHint }}</a><v-icon :size="16">call_missed_outgoing</v-icon></span>
        </span>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import {
  VLayout,
  VFlex,
  VSelect,
  VTextField,
  VIcon
} from 'vuetify/lib';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import utils, { getCountry, setCaretPosition } from './utils';
import clickOutside from './click-outside';
import { mapGetters } from 'vuex';

function getDefault(key) {
  const value = utils.options[key];
  if (typeof value === 'undefined') {
    return utils.options[key];
  }
  return value;
}

export default {
  name: 'VueTelInput',

  directives: {
    clickOutside
  },

  components: {
    VLayout,
    VFlex,
    VSelect,
    VTextField,
    VIcon
  },

  props: {
    value: {
      type: String,
      default: ''
    },
    allCountries: {
      type: Array,
      default: () => getDefault('allCountries')
    },
    autoFormat: {
      type: Boolean,
      default: () => getDefault('autoFormat')
    },
    customValidate: {
      type: [Boolean, RegExp],
      default: () => getDefault('customValidate')
    },
    defaultCountry: {
      // Default country code, ie: 'AU'
      // Will override the current country of user
      type: [String, Number],
      default: () => getDefault('defaultCountry')
    },
    disabled: {
      type: Boolean,
      default: () => getDefault('disabled')
    },
    autoDefaultCountry: {
      type: Boolean,
      default: () => getDefault('autoDefaultCountry')
    },
    dropdownOptions: {
      type: Object,
      default: () => getDefault('dropdownOptions')
    },
    ignoredCountries: {
      type: Array,
      default: () => getDefault('ignoredCountries')
    },
    inputOptions: {
      type: Object,
      default: () => getDefault('inputOptions')
    },
    invalidMsg: {
      type: String,
      default: () => getDefault('invalidMsg')
    },
    showInvalidMessage: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: () => getDefault('mode')
    },
    onlyCountries: {
      type: Array,
      default: () => getDefault('onlyCountries')
    },
    preferredCountries: {
      type: Array,
      default: () => getDefault('preferredCountries')
    },
    validCharactersOnly: {
      type: Boolean,
      default: () => getDefault('validCharactersOnly')
    },
    styleClasses: {
      type: [String, Array, Object],
      default: () => getDefault('styleClasses')
    },
    forSummary: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      phone: '',
      activeCountryCode: '',
      open: false,
      finishMounted: false,
      selectedIndex: null,
      parsedPlaceholder: this.inputOptions.placeholder,
      searchQuery: '',
      hasFocus: this.inputOptions.autofocus,
      selectedCountry: null,
      verifiedNumberHint: 'Number not verified',
      showIcon: false
    };
  },

  computed: {
    ...mapGetters(['businessDetails']),
    activeCountry() {
      return this.findCountry(this.activeCountryCode);
    },
    parsedMode() {
      // Commenting out international formating as we only need national in every case
      if (this.mode === 'auto') {
        if (!this.phone || this.phone[0] !== '+') {
          return 'national';
        }
        //return 'international';
        return 'national';
      }
      //if (!['international', 'national'].includes(this.mode)) {
      if (!['national'].includes(this.mode)) {
        window.console.error('Invalid value of prop "mode"');
        //return 'international';
        return 'national';
      }
      return this.mode;
    },
    filteredCountries() {
      // List countries after filtered
      if (this.onlyCountries.length) {
        return this.allCountries
          .filter(({ iso2 }) => this.onlyCountries.some((c) => c.toUpperCase() === iso2));
      }

      if (this.ignoredCountries.length) {
        return this.allCountries.filter(
          ({ iso2 }) => !this.ignoredCountries.includes(iso2.toUpperCase())
            && !this.ignoredCountries.includes(iso2.toLowerCase()),
        );
      }

      return this.allCountries;
    },
    sortedCountries() {
      // Sort the list countries: from preferred countries to all countries
      const preferredCountries = this.getCountries(this.preferredCountries)
        .map((country) => ({ ...country }));
      const filteredCountries = this.filteredCountries.filter(({ iso2 }) => !preferredCountries.some((c) => c.iso2.toUpperCase() === iso2));
      const countriesList = [...preferredCountries, ...filteredCountries];

      try
      {
        if(this.searchQuery)
        {
          let cleanSearchQuery = this.searchQuery;
          if(cleanSearchQuery.indexOf('+') > -1)
          {
            cleanSearchQuery = this.searchQuery.replace('+', '');
          }

          var filteredList = countriesList.filter(
            (c) => (new RegExp(cleanSearchQuery, 'i')).test(c.name)
          || (new RegExp(cleanSearchQuery, 'i')).test(c.iso2)
          || (new RegExp(cleanSearchQuery, 'i')).test(c.dialCode),
          );

          return filteredList;
        }
        else
        {
          return countriesList;
        }
      }
      catch
      {
        return countriesList;
      }
    },
    phoneObject() {
      let result;
      if (this.phone && this.phone[0] === '+') {
        result = parsePhoneNumberFromString(this.phone) || {};
      } else {
        result = parsePhoneNumberFromString(this.phone, this.activeCountryCode) || {};
      }

      const {
        ...phoneObject
      } = result;

      let valid = (typeof result.isValid === 'function') ? result.isValid() : undefined;
      let formatted = this.phone;

      if (valid) {
        formatted = (typeof result.format === 'function') ? result.format(this.parsedMode.toUpperCase()) : undefined;
      }

      if (result.country && (this.ignoredCountries.length || this.onlyCountries.length)) {
        if (!this.findCountry(result.country)) {
          valid = false;
          Object.assign(result, { country: null });
        }
      }

      Object.assign(phoneObject, {
        countryCode: result.country,
        valid,
        country: this.activeCountry,
        formatted
      });

      return phoneObject;
    },
    inputBorderStyle() {
      return ((!this.phoneObject.valid) && this.showInvalidMessage) ? 'border-bottom: 2px solid red;' : (this.hasFocus || this.open) ? 'border-bottom: 2px solid #32a4a8;' : 'border-bottom: 2px solid #afafaf;';
    }
  },

  watch: {
    activeCountry(value, oldValue) {
      if (!value && oldValue && oldValue.iso2) {
        this.activeCountryCode = oldValue.iso2;
        this.selectedCountry = oldValue;
        this.selectedIndex = this.sortedCountries.findIndex(country => {
          return country.dialCode === oldValue.dialCode;
        });
        return;
      }
      if (value && value.iso2) {
        this.selectedCountry = value;
        this.selectedIndex = this.sortedCountries.findIndex(country => {
          return country.dialCode === value.dialCode;
        });
        this.$emit('country-changed', value);
      }
    },
    'phoneObject.countryCode': function (value) {
      if (!value) {
        return;
      }
      this.activeCountryCode = value || '';
      this.selectedIndex = this.sortedCountries.findIndex(country => {
        return country.dialCode === value.dialCode;
      });
      this.selectedCountry = this.findCountry(this.activeCountryCode);
    },
    'phoneObject.valid': function () {
      this.$emit('validate', this.phoneObject);
    },
    'phoneObject.formatted': function (value) {
      if (!this.autoFormat || this.customValidate) {
        return;
      }
      this.emitInput(value);

      this.$nextTick(() => {
        // In case `v-model` is not set, we need to update the `phone` to be new formatted value
        if (value && !this.value) {
          this.phone = value;
        }
      });
    },
    phone(value, oldValue) {
      if (!this.testCharacters()) {
        this.$nextTick(() => {
          this.phone = oldValue;
          this.onInput();
        });
      } else {
        this.phone = value;
      }
    },
    open(isDropdownOpened) {
      // Emit open and close events
      if (isDropdownOpened) {
        this.$emit('open');
      } else {
        this.$emit('close');
        this.searchQuery = '';
      }
    },
    selectedCountry(value) {
      this.choose(value);
      this.open = false;
    }
  },

  mounted() {
    if(this.activeCountry)
    {
      this.selectedCountry = this.activeCountry;
    }

    if (this.value) {
      this.phone = this.value.trim();
    }

    this.cleanInvalidCharacters();

    this.initializeCountry()
      .then(() => {
        if (!this.phone
          && this.inputOptions
          && this.inputOptions.showDialCode
          && this.activeCountryCode) {
          this.phone = `+${this.activeCountryCode}`;
        }
        this.$emit('validate', this.phoneObject);
      })
      .catch(window.console.error)
      .then(() => {
        this.finishMounted = true;
      });
    if(this.inputOptions.verifiedNumber){
      this.verifiedNumberHint = this.inputOptions.verifiedNumber;
    }
  },

  methods: {
    initializeCountry() {
      return new Promise((resolve) => {
        /**
         * 1. If the phone included prefix (i.e. +12), try to get the country and set it
         */
        if (this.phone && this.phone[0] === '+') {
          resolve();
          return;
        }
        /**
         * 2. Use default country if passed from parent
         */
        if (this.defaultCountry) {
          if (typeof this.defaultCountry === 'string') {
            this.choose(this.defaultCountry);
            resolve();
            return;
          }
          if (typeof this.defaultCountry === 'number') {
            const country = this.findCountryByDialCode(this.defaultCountry);
            if (country) {
              this.choose(country.iso2);
              resolve();
              return;
            }
          }
        }

        const fallbackCountry = this.preferredCountries[0] || this.filteredCountries[0];
        /**
         * 3. Check if fetching country based on user's IP is allowed, set it as the default country
         */
        if (this.autoDefaultCountry) {
          getCountry()
            .then((res) => {
              this.choose(res || this.activeCountryCode);
            })
            .catch((error) => {
              window.console.warn(error);
              /**
               * 4. Use the first country from preferred list (if available) or all countries list
               */
              this.choose(fallbackCountry);
            })
            .then(() => {
              resolve();
            });
        } else {
          /**
           * 4. Use the first country from preferred list (if available) or all countries list
           */
          this.choose(fallbackCountry);
          resolve();
        }
      });
    },
    /**
     * Get the list of countries from the list of iso2 code
     */
    getCountries(list = []) {
      return list
        .map((countryCode) => this.findCountry(countryCode))
        .filter(Boolean);
    },
    findCountry(iso = '') {
      return this.filteredCountries.find((country) => country.iso2 === iso.toUpperCase());
    },
    findCountryByDialCode(dialCode) {
      return this.filteredCountries.find((country) => Number(country.dialCode) === dialCode);
    },
    choose(country) {
      let parsedCountry = country;
      if (typeof parsedCountry === 'string') {
        parsedCountry = this.findCountry(parsedCountry);
      }

      if (!parsedCountry) {
        return;
      }
      if (this.phone && this.phone[0] === '+'
        && parsedCountry.iso2
        && this.phoneObject.nationalNumber) {
        this.activeCountryCode = parsedCountry.iso2;
        // Attach the current phone number with the newly selected country
        this.phone = parsePhoneNumberFromString(
          this.phoneObject.nationalNumber,
          parsedCountry.iso2,
        )
          .formatInternational();
        return;
      }

      if (this.inputOptions && this.inputOptions.showDialCode && parsedCountry) {
        // Reset phone if the showDialCode is set
        this.phone = `+${parsedCountry.dialCode}`;
        this.activeCountryCode = parsedCountry.iso2 || '';
        return;
      }

      // update value, even if international mode is NOT used
      this.activeCountryCode = parsedCountry.iso2 || '';
      this.emitInput(this.phone);
    },
    cleanInvalidCharacters() {
      const currentPhone = this.phone;
      if (this.validCharactersOnly) {
        const results = this.phone.match(/[()\-+0-9\s]*/g);
        this.phone = results.join('');
      }

      if (this.customValidate && this.customValidate instanceof RegExp) {
        const results = this.phone.match(this.customValidate);
        this.phone = results.join('');
      }

      if (currentPhone !== this.phone) {
        this.emitInput(this.phone);
      }
    },
    testCharacters() {
      if (this.validCharactersOnly) {
        const result = /^[()\-+0-9\s]*$/.test(this.phone);
        if (!result) {
          return false;
        }
      }
      if (this.customValidate) {
        return this.testCustomValidate();
      }
      return true;
    },
    testCustomValidate() {
      return this.customValidate instanceof RegExp ? this.customValidate.test(this.phone) : false;
    },
    onInput() {
      this.showInvalidMessage = false;
      // Returns response.number to assign it to v-model (if being used)
      // Returns full response for cases @input is used
      // and parent wants to return the whole response.
      this.emitInput(this.phone);
    },
    emitInput(value) {
      this.$emit('on-input', value, this.phoneObject, this.$refs.input);
    },
    disableIcon(){
      this.showIcon = false;
      this.$refs.input.blur();
    },
    onBlur() {
      this.showIcon = false;
      this.hasFocus = false;
      this.$emit('blur');
    },
    onFocus() {
      this.showIcon = true;
      this.hasFocus = true;
      setCaretPosition(this.$refs.input, this.phone.length);
      this.$emit('focus');
    },
    focus() {
      this.$refs.input.focus();
    },
    openDropdown() {
      if (this.disabled || this.dropdownOptions.disabled) {
        this.open = false;
      }
      else
      {
        this.open = true;
      }
    },
    clickedOutside() {
      this.open = false;
    },
    keyboardNav(e) {
      if(e.keyCode == 27) {
        e.preventDefault();
      }
      else {
        const input = String.fromCharCode(e.keyCode);
        if (/[a-zA-Z0-9-_ +]/.test(input)) {
          // typing a country's name
          if(!this.dropdownOptions.showSearchBox)
          {
            this.searchQuery += e.key;
          }
          else
          {
            this.$refs.countrySearch.focus();
          }
        }
      }
    },
    searchCountryKeydown(e)
    {
      if(e.keyCode == 27) {
        this.$refs.countrySelector.blur();
        this.clickedOutside();
      }
    },
    updatePhoneNumber()
    {
      if(this.inputOptions.verifiedNumber){
        this.phone = this.inputOptions.verifiedNumber.split('-')[1]
      }
    }
  }

};
</script>

<style scoped>
.wrapper {
  background: white !important;
}
::v-deep input::placeholder {
    font-size: 14px !important;
}
::v-deep .country-selector .v-input__slot {
  margin: 0 !important;
  padding: 0 !important;
  background: transparent !important;
}
::v-deep .country-selector .v-text-field__details {
  display:none;
}
::v-deep .country-selector .v-select__selections {
  padding: 0 !important;
}
::v-deep .search-country .v-input__slot {
  margin:0 !important;
}
::v-deep .search-country .v-text-field__details {
  display: none;
}
::v-deep .v-input.v-text-field.country-selector {
  padding:0 !important;
  margin:0 !important;
}
::v-deep .country-selector.v-text-field .v-input__append-inner{
  padding-top: 4px;
}
</style>

<style src="./sprite.css"></style>
<style src="./component.css"></style>
